import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";

import { appApi, useGetAllStaffQuery } from "../../../store/appApi";
import { formatCurrency } from "./netplusConfig";
import { transferNetplusToEmployeeApi } from "../../../services/netplus";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const formatRoleLabel = (role) => String(role || "").replace(/-/g, " ");

const TransferToEmployeeModal = ({ currentBalance, onClose }) => {
  const dispatch = useDispatch();
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(new Date());
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  // Already scoped to the active project server-side (x-project-id header).
  const { data: staffData, isLoading: staffLoading } = useGetAllStaffQuery({
    page: 1,
    limit: 500,
  });
  const employees = Array.isArray(staffData?.staff)
    ? staffData.staff.filter((item) => item?.status !== false)
    : [];

  const handleTransfer = async () => {
    if (!employeeId) {
      Swal.fire({
        icon: "warning",
        title: "Select employee",
        text: "Please choose an employee to transfer to.",
      });
      return;
    }

    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }
    if (numericAmount > currentBalance) {
      Swal.fire({
        icon: "warning",
        title: "Amount too high",
        text: `Only ${formatCurrency(currentBalance)} is available to transfer.`,
      });
      return;
    }

    try {
      setSaving(true);
      await transferNetplusToEmployeeApi({
        employeeId,
        date: date.toISOString(),
        amount: numericAmount,
        remarks,
      });
      dispatch(appApi.util.invalidateTags(["NetplusReceipts", "Imprest"]));
      Swal.fire({
        icon: "success",
        title: "Transferred",
        text: `${formatCurrency(numericAmount)} moved to the employee's imprest.`,
        timer: 1800,
        showConfirmButton: false,
      });
      onClose();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to transfer",
        text:
          error?.response?.data?.message ||
          "Something went wrong while transferring this amount.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-gray-900">
          Transfer to Employee
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          This debits Netplus and credits the employee's own imprest directly
          — it does not create or affect any entry in the main Daybook.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className={labelClass}>Employee</label>
            <select
              className={inputClass}
              autoComplete="off"
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
              disabled={staffLoading}
            >
              <option value="">
                {staffLoading ? "Loading employees…" : "Select employee"}
              </option>
              {employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.name} - {formatRoleLabel(employee.role)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <DatePicker
              selected={date}
              onChange={(value) => value && setDate(value)}
              className={inputClass}
              autoComplete="off"
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
            />
          </div>
          <div>
            <label className={labelClass}>Amount</label>
            <input
              type="number"
              min="0"
              max={currentBalance}
              step="0.01"
              className={inputClass}
              autoComplete="off"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0.00"
            />
            <p className="mt-1 text-xs text-gray-500">
              Available balance:{" "}
              <span className="font-semibold text-gray-900">
                {formatCurrency(currentBalance)}
              </span>
            </p>
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <input
              className={inputClass}
              autoComplete="off"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              placeholder="Optional note"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-full bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={handleTransfer}
            disabled={saving || currentBalance <= 0}
          >
            {saving ? "Transferring..." : "Confirm Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferToEmployeeModal;
